import { useHandler } from "@chatbotgang/etude/react/useHandler";
import OpenInNewOutlined from "@mui/icons-material/OpenInNewOutlined";
import UnfoldMoreOutlined from "@mui/icons-material/UnfoldMoreOutlined";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import ListSubheader from "@mui/material/ListSubheader";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import type { SxProps } from "@mui/material/styles";
import { CAAC_URL, DAAC_URL, MAAC_URL } from "@polifonia/env";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { PLATFORM_NAME } from "@/appConstant";
import { Typography } from "@/components/Typography";
import { trackGaEvent } from "@/features/ga/trackGaEvent";
import CL_LOGO from "@/statics/CresclabIcon.png";

const styles = {
  trigger: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    width: "100%",
    padding: "8px",
    borderRadius: "6px",
    textAlign: "left",
    "&:hover": {
      backgroundColor: theme.colors.neutral010,
    },
  },
  compactTrigger: {
    borderRadius: "8px",
  },
  logo: {
    width: "32px",
    height: "32px",
    flex: "0 0 32px",
    borderRadius: "8px",
    backgroundColor: theme.colors.neutral100,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  logoImage: {
    width: "20px",
    height: "20px",
  },
  text: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
  },
  productName: {
    lineHeight: 1.3,
    overflowWrap: "anywhere",
  },
  ellipsis: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  unfold: {
    fontSize: "1rem",
    color: theme.colors.staticFgNote,
  },
  menuItem: {
    display: "flex",
    justifyContent: "space-between",
    gap: "24px",
    minWidth: "220px",
  },
  menuIcon: {
    fontSize: "1rem",
    color: theme.colors.staticFgNote,
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

/** The other Crescendo products, opened in a new tab. */
const products = [
  { name: "MAAC", url: MAAC_URL },
  { name: "CAAC", url: CAAC_URL },
  { name: "DAAC", url: DAAC_URL },
].filter((product) => Boolean(product.url));

interface ProductSwitcherProps {
  organizationName: string | undefined;
  /** Shows only the logo, for the collapsed side menu. */
  compact: boolean;
}

/**
 * The top of the side menu: the platform's name and the org, and a menu to
 * open the other Crescendo products.
 */
export const ProductSwitcher: React.FC<ProductSwitcherProps> =
  function ProductSwitcher({ organizationName, compact }) {
    const { t } = useTranslation();
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);

    const handleOpen = useHandler<React.MouseEventHandler<HTMLButtonElement>>(
      (event) => setAnchor(event.currentTarget),
    );
    const handleClose = useHandler(() => setAnchor(null));

    return (
      <>
        <ButtonBase
          sx={compact ? styles.compactTrigger : styles.trigger}
          onClick={handleOpen}
          aria-label={t("sideMenu.switchProduct", "Switch product")}
          aria-haspopup="menu"
          aria-expanded={Boolean(anchor)}
        >
          <Box sx={styles.logo}>
            <Box component="img" src={CL_LOGO} alt="" sx={styles.logoImage} />
          </Box>
          {compact ? null : (
            <>
              <Box sx={styles.text}>
                {/* Wraps rather than truncates: the full name is the brand. */}
                <Box sx={styles.productName}>
                  <Typography variant="body" fontWeight={600}>
                    {PLATFORM_NAME}
                  </Typography>
                </Box>
                {organizationName ? (
                  <Box sx={styles.ellipsis}>
                    <Typography
                      variant="note"
                      color={theme.colors.staticFgNote}
                    >
                      {organizationName}
                    </Typography>
                  </Box>
                ) : null}
              </Box>
              <UnfoldMoreOutlined sx={styles.unfold} />
            </>
          )}
        </ButtonBase>
        <Menu
          anchorEl={anchor}
          open={Boolean(anchor)}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <ListSubheader>
            {t("sideMenu.switchProduct", "Switch product")}
          </ListSubheader>
          {products.map((product) => (
            <MenuItem
              key={product.name}
              component="a"
              href={product.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                trackGaEvent("productSwitchClick", { label: product.name });
                handleClose();
              }}
              sx={styles.menuItem}
            >
              {product.name}
              <OpenInNewOutlined sx={styles.menuIcon} />
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  };
